import { TFunction } from 'i18next'
import KeyManagementRestService from '../rest/KeyManagementRestService'

class KeyManagementService {
  static async getKeys(setKeys: Function) {
    const keys = await KeyManagementRestService.getVisibleKeys()
    setKeys(keys)
  }

  static async getKeyRotation(setKeyRotation: Function) {
    const keyRotation = await KeyManagementRestService.getKeyRotation()
    setKeyRotation(keyRotation)
  }

  static async createKeys(keys: any, setKeys: Function, t: TFunction): Promise<any> {
    if (keys.mode === 'Random Keys') {
      return KeyManagementService.createRandomKeys(setKeys, t)
    } else if (keys.mode === 'Luna HSM') {
      return KeyManagementService.createLunaHsmKey(keys, setKeys, t)
    } else if (keys.mode === 'Utimaco HSM') {
      return KeyManagementService.createUtimacoHsmKey(keys, setKeys, t)
    }
  }

  static async createRandomKeys(setKeys: Function, t: TFunction): Promise<any> {
    try {
      await KeyManagementRestService.createRandomKeys()
      await KeyManagementService.getKeys(setKeys)
      return t('keys.createRandomKeysSuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async createLunaHsmKey(key: any, setKeys: Function, t: TFunction): Promise<any> {
    const lunaKey = {
      algorithm: 'AES/CBC/PKCS5Padding',
      keyInfo: {
        keyAlias: key.keyAlias,
        partition: key.partition,
        password: key.password,
      },
      name: key.name,
      visible: true,
    }
    try {
      await KeyManagementRestService.createLunaHsmKey(lunaKey)
      await KeyManagementService.getKeys(setKeys)
      return t('keys.createLunaSuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async createUtimacoHsmKey(key: any, setKeys: Function, t: TFunction): Promise<any> {
    try {
      const utimacoKey = {
        algorithm: 'AES/CBC/PKCS5Padding',
        keyInfo: {
          connectionTimeout: '3000',
          timeout: '3000',
          endSessionOnShutdown: '1',
          keepSessionAlive: '0',
          device: key.device,
          defaultUser: key.defaultUser,
          password: key.password,
          keyName: key.keyName,
          keyGroup: key.keyGroup,
          keySpecifier: key.keySpecifier,
        },
        name: key.name,
        visible: true,
      }
      await KeyManagementRestService.createUtimacoHsmKey(utimacoKey)
      await KeyManagementService.getKeys(setKeys)
      return t('keys.createUtimacoSuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async editLunaHsmKey(key: any, setKeys: Function, t: TFunction): Promise<any> {
    try {
      const lunaKey = {
        algorithm: 'AES/CBC/PKCS5Padding',
        keyId: key.keyId,
        keyInfo: {
          keyAlias: key.keyAlias,
          partition: key.partition,
          password: key.password,
        },
        name: key.name,
        keyVersion: key.keyVersion,
        shortKeyId: key.shortKeyId,
        visible: true,
      }
      await KeyManagementRestService.editLunaHsmKey(lunaKey)
      await KeyManagementService.getKeys(setKeys)
      return t('keys.editLunaSuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async editUtimacoHsmKey(key: any, setKeys: Function, t: TFunction): Promise<any> {
    try {
      await KeyManagementRestService.editUtimacoHsmKey(key)
      await KeyManagementService.getKeys(setKeys)
      return t('keys.editUtimacoSuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async changeKeyRotation(keyRotationSetting: any, setKeyRotation: React.Dispatch<any>, t: TFunction): Promise<any> {
    try {
      await KeyManagementRestService.changeKeyRotation(keyRotationSetting)
      await KeyManagementService.getKeyRotation(setKeyRotation)
      return t('keys.changeKeyRotationSuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async hideHsmKey(keyId: string, version: number, setKeys: Function, t: TFunction) {
    try {
      await KeyManagementRestService.hideHsmKey(keyId, version)
      await KeyManagementService.getKeys(setKeys)
      return t('keys.hideHsmSuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }

  static async hideLegacyKey(keyId: string, version: number, setKeys: Function, t: TFunction) {
    try {
      await KeyManagementRestService.hideLegacyKey(keyId, version)
      await KeyManagementService.getKeys(setKeys)
      return t('keys.hideLegacySuccess')
    } catch (error) {
      return Promise.reject(error)
    }
  }
}

export default KeyManagementService
